import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import type { ContractFormValues } from '../schemas';

interface BasicInfoSectionProps {
  isFieldDisabled: boolean;
  clientIdFromUrl: string | null;
}

export function BasicInfoSection({ isFieldDisabled, clientIdFromUrl }: BasicInfoSectionProps) {
  const { t } = useTranslation();
  const form = useFormContext<ContractFormValues>();

  return (
    <div className="space-y-4">
      {/* Contract Type - Full width */}
      <FormField
        name="type"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('type')} *</FormLabel>
            <FormControl>
              <Select
                disabled={isFieldDisabled}
                value={field.value || 'HOURLY'}
                onValueChange={field.onChange}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={t('selectType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HOURLY">{t('hourly')}</SelectItem>
                  <SelectItem value="FIXED">{t('fixed')}</SelectItem>
                  <SelectItem value="SUCCESS_FEE">{t('successFee')}</SelectItem>
                  <SelectItem value="PRO_BONO">{t('proBono')}</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Reason & Practice Area */}
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          name="reason"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('reason')}</FormLabel>
              <FormControl>
                <Select
                  disabled={isFieldDisabled}
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder={t('reason')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USTAV">{t('ustav')}</SelectItem>
                    <SelectItem value="DOVERNOST">{t('dovernost')}</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="practiceAreaId"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('contractPracticeArea')} *</FormLabel>
              <FormControl>
                <UniversalCombobox
                  url="/contract-practice-areas"
                  labelKey="name"
                  valueKey="id"
                  selectedId={field.value}
                  onSelectItem={(item) => field.onChange(item?.['id'] ?? null)}
                  placeholder={t('contractPracticeArea')}
                  disabled={isFieldDisabled}
                  className="bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Client */}
      <FormField
        name="clientId"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('client')} *</FormLabel>
            <FormControl>
              <UniversalCombobox
                url="/clients"
                labelKey="name"
                labelKeys={['lastname', 'name', 'middlename']}
                valueKey="id"
                selectedId={field.value}
                onSelectItem={(item) => field.onChange(item?.['id'] ?? null)}
                placeholder={t('selectClient')}
                disabled={isFieldDisabled || !!clientIdFromUrl}
                className="bg-white"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Dates */}
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          name="startsAt"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('startsAt')} *</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isFieldDisabled}
                  mode="date"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="endsAt"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('endsAt')}</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isFieldDisabled}
                  mode="date"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
